import { Injectable, signal } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { type PmSortDirection } from '../../../shared/components/pm-data-grid/pm-data-grid.component';
import { type CompaniesHouseProfileApi, type CompaniesHouseNotificationItemApi } from '../../../core/services/api/pm-companies-house-api.service';
import { type DirectoryClientRowApi } from '../../../core/services/api/pm-directory-api.service';
import type { ChPageRow, ChPeopleTab, ChDetailMainTab, ChFilingHistoryRow, ChPersonRow } from '../../../layout/pm-workspace/pm-workspace.models';

/**
 * Companies House Clients page state (list, paging, search, open client detail, people tab, loaded flags) kept between visits to the page.
 *
 * Provided on `PmWorkspaceComponent` (not root) so it lives and dies with the workspace,
 * exactly like the fields it replaced.
 */
@Injectable()
export class PmCompaniesHouseClientsStore {
  /** Set by the dashboard shortcut so the page loads the practice-wide CH notifications once on open. */
  loadNotificationsOnOpen = false;

  chPeopleActiveTab = signal<ChPeopleTab>('Directors');

  chPeopleSortDir = signal<PmSortDirection | null>(null);

  // ── Companies House People (aggregated across all Limited/LLP clients) ───────────────────

  chPeopleSortKey = signal<string | null>(null);

  chPeopleRows = signal<ChPersonRow[]>([]);

  chPeopleFilterCountry = signal('');

  chPeopleFilterNationality = signal('');

  chPeopleFilterOccupation = signal('');

  chPeopleFilterCompany = signal('');

  chPeopleSearchQuery = signal('');

  chPersonDetailRow = signal<ChPersonRow | null>(null);

  chPeoplePageNumber = signal(1);

  chPeopleFilterPanelOpen = signal(false);

  chPeopleFilterCountryDraft = signal('');

  chPeopleFilterNationalityDraft = signal('');

  chPeopleFilterOccupationDraft = signal('');

  /** Draft selections for the filter popover's pm-form-custom-select dropdowns — only committed to the
   *  applied chPeopleFilter* signals above when "Apply Filters" is clicked, matching the previous
   *  native-<select>-read-on-apply behavior. */
  chPeopleFilterCompanyDraft = signal('');

  chPeopleCompanyNumberCache = new Map<string, string>();

  chPeopleClientsPromise: Promise<DirectoryClientRowApi[]> | null = null;

  chPeopleError = signal(false);

  chPeopleLoading = signal(false);

  chPeoplePscRequestSeq = 0;

  chPeoplePscLoaded = false;

  chPeopleOfficersRequestSeq = 0;

  /** Officers (Directors + Secretaries share one CH endpoint) and PSC (Shareholders + PSC share one CH
   *  endpoint) are loaded independently, only when a tab that needs that group is first opened. */
  chPeopleOfficersLoaded = false;

  // Companies House — People view (Directors / Shareholders / Secretaries / PSC)
  chViewMode = signal<'Clients' | 'People'>('Clients');

  chClientDetailRows = signal<ChPersonRow[]>([]);

  chClientDetailError = signal(false);

  chClientDetailLoading = signal(false);

  chClientDetailPscRequestSeq = 0;

  // Companies House — single-client detail page (Directors / Shareholders / Secretaries / PSC for one client)
  chClientDetailRow = signal<ChPageRow | null>(null);

  chClientDetailPscLoaded = false;

  chClientDetailShareholdersRequestSeq = 0;

  chClientDetailShareholdersLoaded = false;

  chClientDetailOfficersRequestSeq = 0;

  chClientDetailOfficersLoaded = false;

  chClientDetailSortDir = signal<PmSortDirection | null>(null);

  // ── Companies House single-client People tabs ────────────────────────────────────────────

  chClientDetailSortKey = signal<string | null>(null);

  chClientDetailPageNumber = signal(1);

  chClientDetailActiveTab = signal<ChPeopleTab>('Directors');

  // Companies House — single-client "Filing History" tab
  chClientDetailFilingRows = signal<readonly ChFilingHistoryRow[]>([]);

  chFilingHistoryError = signal(false);

  chFilingHistoryLoading = signal(false);

  chClientDetailFilingRequestSeq = 0;

  chClientDetailFilingLoaded = false;

  chClientDetailProfile = signal<CompaniesHouseProfileApi | null>(null);

  chClientDetailProfileError = signal(false);

  chClientDetailProfileLoading = signal(false);

  chClientDetailProfileRequestSeq = 0;

  chClientDetailProfileLoaded = false;

  // Companies House — single-client "Company Profile" overview tab (Basic Info / Accounts /
  // Confirmation Statement / Registered Address / SIC / Previous Names cards)
  chClientDetailMainTab = signal<ChDetailMainTab>('Company Profile');

  /** Companies House WebFiling auth code — not available via the CH public API, so it's fetched
   *  separately from our own Capium3-stored client record (pmDirectoryApi.getClient). */
  chClientDetailAuthCode = signal<string | null>(null);

  chClientDetailAuthCodeRequestSeq = 0;

  chFilingSortDir = signal<PmSortDirection | null>(null);

  chFilingSortKey = signal<string | null>(null);

  chFilingPageNumber = signal(1);

  chClientsRequestSeq = 0;

  chClientsSortDir = signal<PmSortDirection | null>(null);

  chClientsSortKey = signal<string | null>(null);

  /** Which tab's field layout the person-detail modal should render — passed explicitly by whichever
   *  view opened it (People page or single-client detail page) instead of reading ambient tab state,
   *  so the modal renders correctly regardless of which view is currently active. */
  chPersonDetailTab = signal<ChPeopleTab>('Directors');

  readonly chFilingPreviewUrl = signal<SafeResourceUrl | null>(null);

  // Companies House Filing History — in-app document preview modal (View button). Points the iframe
  // straight at pm-api's document endpoint rather than downloading a blob first, since that endpoint
  // needs no auth and already streams the right Content-Type.
  readonly chFilingPreviewOpen = signal(false);

  chFilingFilterPanelOpen = signal(false);

  chFilingFilterAppliedTo = signal('');

  chFilingFilterAppliedFrom = signal('');

  chFilingFilterAppliedGroups = signal<ReadonlySet<string>>(new Set<string>());

  chFilingFilterDraftTo = signal('');

  chFilingFilterDraftFrom = signal('');

  chFilingFilterDraftGroups = signal<ReadonlySet<string>>(new Set<string>());

  readonly chFilingPreviewName = signal('');

  chClientDetailStatusFilter = signal<'All' | 'Active' | 'Resigned'>('All');

  chClientDetailFilterOpen = signal(false);

  /** Quick-search text and Status filter for the People tab toolbar (design Frame 65). */
  chClientDetailSearch = signal('');

  // Companies House Clients page
  chClientsRows = signal<ChPageRow[]>([]);

  chClientsLoading = signal(false);

  chClientsError = signal(false);

  chClientsPageNumber = signal(1);

  chClientsPageSize = signal(25);

  chClientsTotalCount = signal(0);

  chClientsSearchQuery = signal('');

  chClientDetailPageSize = signal(25);

  chPeoplePageSize = signal(25);

  chFilingPageSize = signal(25);

  /** Grouped by (normalized) company number — the Clients list's "Updates" column reads this to
   *  decide whether a row has anything to show at all, and passes the matching slice straight into
   *  <pm-ch-notifications-popover variant="link"> so hovering shows it with no extra request.
   *  Loaded once per visit to this screen (not per page/sort change): it's practice-wide, not
   *  scoped to whichever page of clients happens to be showing. */
  chClientsNotificationsByCompany = signal<ReadonlyMap<string, readonly CompaniesHouseNotificationItemApi[]>>(new Map());
}
